<script lang="ts" setup>
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

  import { useFirebaseApp } from 'vuefire'

  import { fetchAndActivate, getRemoteConfig, getString, onConfigUpdate } from 'firebase/remote-config'
  import type { ConfigUpdate } from 'firebase/remote-config'

  const props = defineProps<{
    expires: number | null
  }>()

  const emits = defineEmits<{
    login: []
  }>()

  const sessionExpirationInMinutes = ref(
    props.expires === null ? 24 * 60 : Math.max(0, Math.round((props.expires - Date.now()) / 60 / 1000)),
  )

  const sessionExpiring = computed(() => props.expires !== null && sessionExpirationInMinutes.value <= 30)

  let sessionExpirationInterval: number | null = null

  watch(
    () => props.expires,
    () => {
      if (sessionExpirationInterval !== null) {
        window.clearInterval(sessionExpirationInterval)
      }

      const updateSessionExpiration = () => {
        if (props.expires !== null) {
          sessionExpirationInMinutes.value = Math.max(0, Math.round((props.expires - Date.now()) / 60 / 1000))
        } else {
          sessionExpirationInMinutes.value = 24 * 60
        }
      }

      updateSessionExpiration()

      sessionExpirationInterval = window.setInterval(updateSessionExpiration, 1000)
    },
    { immediate: true },
  )

  const issueResolved = ref(false)
  const statusDismissed = ref(false)

  let unsubscribeRemoteConfig: (() => void) | null = null

  const remoteConfig = getRemoteConfig(useFirebaseApp())

  const statusAlert = ref(getString(remoteConfig, 'statusAlertText'))

  onMounted(() => {
    unsubscribeRemoteConfig = onConfigUpdate(remoteConfig, {
      next: async (update: ConfigUpdate) => {
        if (update.getUpdatedKeys().has('statusAlertText')) {
          await fetchAndActivate(remoteConfig)

          const newStatusAlert = getString(remoteConfig, 'statusAlertText')

          issueResolved.value = !!statusAlert.value && !newStatusAlert

          statusAlert.value = newStatusAlert.trim()

          statusDismissed.value = false
        }
      },
      error: (error: Error) => {
        console.error(error)
      },
      complete: () => {},
    })
  })

  onBeforeUnmount(() => {
    if (sessionExpirationInterval !== null) {
      window.clearInterval(sessionExpirationInterval)
    }

    unsubscribeRemoteConfig?.()
  })
</script>

<template lang="html">
  <v-footer v-if="sessionExpiring" app border elevation="1" class="bg-surface pa-0" :order="2">
    <o-alert tile type="info">
      Your session is expiring in
      <b>{{ sessionExpirationInMinutes }}</b>
      minutes, please refresh your session by
      <a href="#" class="text-info font-weight-bold" @click="emits('login')">logging in</a>
      again or you will be logged out automatically.
    </o-alert>
  </v-footer>

  <v-footer
    v-else-if="(statusAlert.trim() || issueResolved) && !statusDismissed"
    app
    border
    elevation="1"
    class="bg-surface breaking-news pa-0"
    :order="3"
  >
    <o-alert tile color="warning" class="breaking-news__alert text-black">
      <div role="status" aria-label="Breaking news" class="breaking-news__viewport">
        <div class="breaking-news__track">
          <span>
            <v-icon class="mt-n1 mr-2" icon="mdi-alert-box" :color="issueResolved ? 'success' : 'warning'" />
            <b>Service disruption{{ issueResolved ? ' over' : '' }}:</b>
            {{
              !issueResolved
                ? statusAlert
                : 'Previously announced issue has been resolved and all should be operating normally'
            }}
          </span>
        </div>
      </div>

      <template #append>
        <o-btn-text class="my-n4" icon="mdi-close" @click="statusDismissed = true" />
      </template>
    </o-alert>
  </v-footer>
</template>

<style lang="scss" scoped>
  .breaking-news__alert {
    width: 100%;
    height: 100%;
  }

  .breaking-news__viewport {
    position: relative;
    flex: 1;
    width: 100%;
    min-width: 100%;
    overflow: hidden;
    white-space: nowrap;
  }

  .breaking-news__track {
    display: flex;
    animation: breaking-news-scroll 20s linear infinite;
    margin-left: 100%;
    width: max-content;

    span {
      padding-inline: 3rem;
    }
  }

  @keyframes breaking-news-scroll {
    to {
      transform: translate(-100%);
      margin-left: 0;
    }
  }
</style>
